import type { ParseKeys } from 'i18next';

/**
 * The values the Pulpit and Statistics would show that no endpoint supplies. One module,
 * so removing the last of them is one deletion. Each entry names what would replace it
 * and the badge it carries meanwhile.
 */

/**
 * Off unless `VITE_SHOW_DEMO_DATA` is set to `true`, which no committed env file does.
 * Compared against a literal so the build inlines it and drops the branches it gates.
 */
export const showDemoData = import.meta.env.VITE_SHOW_DEMO_DATA === 'true';

/** Replaced by the appointments domain. Badged NOT PORTED. */
export const demoAppointments = 64;

/**
 * Text is held as `statistics` dictionary keys, not strings: `t()` at module level would run
 * once at import and never follow the language, and the numbers are the only fabricated part.
 */
type StatisticsKey = ParseKeys<'statistics'>;

/** Counts fixed, not scaled by the range.
 *  Replaced by: Etap 2 row 13, question clustering. Badged NO SUCH FEATURE. */
export const demoResidentQuestions = [
  { question: 'demo.questions.childIdCard', occurrences: 34 },
  { question: 'demo.questions.bulkyWaste', occurrences: 28 },
  { question: 'demo.questions.stampDuty', occurrences: 24 },
  { question: 'demo.questions.officeAfterHoliday', occurrences: 21 },
  { question: 'demo.questions.suspendBusiness', occurrences: 17 },
] as const satisfies readonly { question: StatisticsKey; occurrences: number }[];

/** Replaced once question capture is usable enough for a clerk to act on a row.
 *  Badged NOT PORTED. */
export const demoKnowledgeGaps = [
  { question: 'demo.gaps.cardPayment', occurrences: 14, folder: 'demo.folders.finance' },
  { question: 'demo.gaps.streetLight', occurrences: 11, folder: 'demo.folders.utilities' },
  { question: 'demo.gaps.drivingLicence', occurrences: 9, folder: 'demo.folders.transport' },
  { question: 'demo.gaps.boilerGrant', occurrences: 8, folder: 'demo.folders.general' },
  { question: 'demo.gaps.largeFamilyCard', occurrences: 6, folder: 'demo.folders.general' },
] as const satisfies readonly {
  question: StatisticsKey;
  occurrences: number;
  folder: StatisticsKey;
}[];

/** Counts and shares both fixed, so no real series is mixed with a demo one.
 *  Replaced by: Etap 2 row 2, a handoff-reason taxonomy. Badged NOT PORTED. */
export const demoHandoffReasons = [
  { reason: 'demo.handoffReasons.residentRecord', cases: 67, share: 38 },
  { reason: 'demo.handoffReasons.askedForClerk', cases: 48, share: 27 },
  { reason: 'demo.handoffReasons.noAnswer', cases: 37, share: 21 },
  { reason: 'demo.handoffReasons.complexCase', cases: 24, share: 14 },
] as const satisfies readonly { reason: StatisticsKey; cases: number; share: number }[];
